import React, { useState } from "react"

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  SettingRow,
} from "@coval/ui"
import { Headphones } from "lucide-react"

import { ReadOnlyFieldset } from "@/components/version-history/ReadOnlyRegion"
import {
  isAudioDegradationPresetId,
  isSituateSpeakerPresetId,
  type VoicePersonaConfig,
} from "@/types/ModelConfigs/VoiceModelConfigs"

import type { PersonaFormController } from "../hooks/usePersonaFormController"
import {
  AUDIO_DEGRADATION_SITUATE_SPEAKER_CONFLICT_MESSAGE,
  hasSituateSpeaker,
} from "../utils/personaValidation"
import {
  AUDIO_DEGRADATION_EXCLUSIVITY_NOTE,
  AUDIO_DEGRADATION_OPTIONS,
  CALL_ENVIRONMENT_UNSET_VALUE,
  SITUATE_SPEAKER_BACKGROUND_SOUND_NOTE,
  SITUATE_SPEAKER_OPTIONS,
} from "./callEnvironmentOptions"
import {
  AcousticSourceTypeTag,
  getBackgroundSoundAcousticSourceType,
  getBackgroundSoundDescription,
  PersonaBackgroundNoiseDialog,
} from "./PersonaBackgroundNoiseDialog"
import {
  OptionCard,
  PersonaOptionPickerDialog,
} from "./PersonaOptionPickerDialog"

type EnvironmentPicker = "background" | "situate" | "degradation"

function EnvironmentRow({
  label,
  tooltip,
  selected,
  error,
  disabled,
  currentlyPlayingAudioId,
  onPlayingChange,
  onOpen,
}: {
  label: string
  tooltip: React.ReactNode
  /** The current choice, shown on the card's own surface. */
  selected: {
    label: string
    description?: string
    badge?: React.ReactNode
    previewUrl?: string
    previewVolume?: number
  }
  error?: string | null
  disabled: boolean
  currentlyPlayingAudioId: string | null
  onPlayingChange: (playingId: string | null) => void
  onOpen: () => void
}) {
  return (
    <SettingRow label={label} tooltip={tooltip} stacked error={error}>
      <OptionCard
        label={selected.label}
        description={selected.description}
        badge={selected.badge}
        previewUrl={selected.previewUrl}
        previewVolume={selected.previewVolume}
        isSelected={false}
        isDisabled={disabled}
        currentlyPlayingAudioId={currentlyPlayingAudioId}
        onPlayingChange={onPlayingChange}
        onSelect={onOpen}
        // The current choice alone reads as a fixed value, so the row names
        // the action that opens the full list of options.
        action={
          <Button
            type="button"
            variant="secondary"
            onClick={onOpen}
            disabled={disabled}
            aria-label={`Change ${label}`}
            className="h-7 px-2 text-xs"
          >
            Change
          </Button>
        }
        variant="plain"
      />
    </SettingRow>
  )
}

/**
 * Background noise, speaker placement, and line quality as three settings
 * rows, each opening a picker of option cards. Mirrors the Voice card so the
 * whole audio side of a persona is configured the same way.
 */
export function PersonaCallEnvironmentCard({
  controller,
}: {
  controller: PersonaFormController
}) {
  const {
    currentlyPlayingAudioId,
    formData,
    isSpeechDisabled,
    selectedBackgroundSound,
    setCurrentlyPlayingAudioId,
    updateMetadata,
  } = controller
  const [openPicker, setOpenPicker] = useState<EnvironmentPicker | null>(null)
  const metadata = formData.metadata as VoicePersonaConfig

  const situateSpeaker = isSituateSpeakerPresetId(metadata.situate_speaker)
    ? metadata.situate_speaker
    : CALL_ENVIRONMENT_UNSET_VALUE
  const situateOption = SITUATE_SPEAKER_OPTIONS.find(
    (option) => option.value === situateSpeaker,
  )

  const audioDegradation = metadata.audio_degradation
  const isRecognizedPreset = isAudioDegradationPresetId(
    audioDegradation?.preset,
  )
  const unknownPreset =
    audioDegradation != null && !isRecognizedPreset
      ? String(audioDegradation.preset)
      : null
  const degradationValue = isRecognizedPreset
    ? audioDegradation.preset
    : CALL_ENVIRONMENT_UNSET_VALUE
  const degradationOption = AUDIO_DEGRADATION_OPTIONS.find(
    (option) => option.value === degradationValue,
  )
  const degradationConflict =
    audioDegradation != null && hasSituateSpeaker(formData.metadata)
      ? AUDIO_DEGRADATION_SITUATE_SPEAKER_CONFLICT_MESSAGE
      : null

  const backgroundAcousticSourceType = selectedBackgroundSound
    ? getBackgroundSoundAcousticSourceType(selectedBackgroundSound)
    : undefined
  const backgroundPreviewVolume =
    metadata.background_sound_volume ??
    selectedBackgroundSound?.default_volume ??
    undefined

  const close = () => {
    setCurrentlyPlayingAudioId(null)
    setOpenPicker(null)
  }

  return (
    <>
      <Card className="w-full">
        <ReadOnlyFieldset>
          <CardHeader className="p-6 pb-2">
            <CardTitle className="flex items-center gap-2">
              <Headphones className="size-4 shrink-0" />
              Call Environment
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 px-6 pb-4 pt-0">
            <EnvironmentRow
              label="Background Noise"
              tooltip="Ambient sound mixed into the persona's side of the call."
              selected={{
                label: selectedBackgroundSound?.display_name ?? "Off",
                description: selectedBackgroundSound
                  ? getBackgroundSoundDescription(selectedBackgroundSound)
                  : "No background noise on the call.",
                badge: backgroundAcousticSourceType ? (
                  <AcousticSourceTypeTag type={backgroundAcousticSourceType} />
                ) : undefined,
                previewUrl: selectedBackgroundSound?.preview_url ?? undefined,
                previewVolume: backgroundPreviewVolume,
              }}
              disabled={isSpeechDisabled}
              currentlyPlayingAudioId={currentlyPlayingAudioId}
              onPlayingChange={setCurrentlyPlayingAudioId}
              onOpen={() => setOpenPicker("background")}
            />
            <EnvironmentRow
              label="Situate Speaker"
              tooltip={
                <>
                  Where the persona sounds like they are speaking from.{" "}
                  {SITUATE_SPEAKER_BACKGROUND_SOUND_NOTE}
                </>
              }
              selected={{
                label: situateOption?.label ?? "Clean speech",
                description: situateOption?.description,
                previewUrl: situateOption?.previewUrl,
              }}
              disabled={isSpeechDisabled}
              currentlyPlayingAudioId={currentlyPlayingAudioId}
              onPlayingChange={setCurrentlyPlayingAudioId}
              onOpen={() => setOpenPicker("situate")}
            />
            <EnvironmentRow
              label="Audio Degradation"
              tooltip={
                <>
                  Simulate a degraded phone connection on the persona's voice.{" "}
                  {AUDIO_DEGRADATION_EXCLUSIVITY_NOTE}
                </>
              }
              selected={
                unknownPreset != null
                  ? {
                      label: `Unknown preset “${unknownPreset}”`,
                      description:
                        "This preset is not available in this app version. It remains configured unless you choose another option.",
                    }
                  : {
                      label: degradationOption?.label ?? "Clean line",
                      description: degradationOption?.description,
                      previewUrl: degradationOption?.previewUrl,
                    }
              }
              error={degradationConflict}
              disabled={isSpeechDisabled}
              currentlyPlayingAudioId={currentlyPlayingAudioId}
              onPlayingChange={setCurrentlyPlayingAudioId}
              onOpen={() => setOpenPicker("degradation")}
            />
          </CardContent>
        </ReadOnlyFieldset>
      </Card>

      <PersonaBackgroundNoiseDialog
        controller={controller}
        isOpen={openPicker === "background"}
        onClose={close}
      />

      <PersonaOptionPickerDialog
        isOpen={openPicker === "situate"}
        title="Situate Speaker"
        description={SITUATE_SPEAKER_BACKGROUND_SOUND_NOTE}
        onClose={close}
      >
        {SITUATE_SPEAKER_OPTIONS.map((option) => (
          <OptionCard
            key={option.value}
            label={option.label}
            description={option.description}
            previewUrl={option.previewUrl}
            isSelected={situateSpeaker === option.value}
            isDisabled={isSpeechDisabled}
            currentlyPlayingAudioId={currentlyPlayingAudioId}
            onPlayingChange={setCurrentlyPlayingAudioId}
            onSelect={() =>
              updateMetadata({
                situate_speaker:
                  option.value === CALL_ENVIRONMENT_UNSET_VALUE
                    ? undefined
                    : option.value,
              })
            }
          />
        ))}
      </PersonaOptionPickerDialog>

      <PersonaOptionPickerDialog
        isOpen={openPicker === "degradation"}
        title="Audio Degradation"
        description={AUDIO_DEGRADATION_EXCLUSIVITY_NOTE}
        onClose={close}
        banner={
          degradationConflict ? (
            <p className="text-xs text-coval-danger-text">
              {degradationConflict}
            </p>
          ) : undefined
        }
      >
        {unknownPreset != null && (
          <OptionCard
            label={`Unknown preset “${unknownPreset}” (kept)`}
            description="This preset is not available in this app version. It remains configured unless you choose another option."
            isSelected
            isDisabled
            currentlyPlayingAudioId={currentlyPlayingAudioId}
            onPlayingChange={setCurrentlyPlayingAudioId}
            onSelect={() => {}}
          />
        )}
        {AUDIO_DEGRADATION_OPTIONS.map((option) => (
          <OptionCard
            key={option.value}
            label={option.label}
            description={option.description}
            previewUrl={option.previewUrl}
            isSelected={
              unknownPreset == null && degradationValue === option.value
            }
            isDisabled={isSpeechDisabled}
            currentlyPlayingAudioId={currentlyPlayingAudioId}
            onPlayingChange={setCurrentlyPlayingAudioId}
            onSelect={() =>
              updateMetadata({
                audio_degradation:
                  option.value === CALL_ENVIRONMENT_UNSET_VALUE
                    ? undefined
                    : { preset: option.value },
              })
            }
          />
        ))}
      </PersonaOptionPickerDialog>
    </>
  )
}
